import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import cameras from "../data/cameras";
import recipes from "../data/recipes-curated.json";
import parameterPages from "../data/parameter-pages.json";
import Footer from "../components/Footer";
import Icon from "../components/Icon";
import { usePublished } from "../utils/publishing";
import { apertureSummary, ndSummary } from "../utils/cameraFacts";
import { setPageMeta, breadcrumbLd } from "../utils/meta";

/**
 * The index of every camera in the database.
 *
 * This page exists because of a measurement, not a hunch: before it, 139
 * of 145 camera hubs had no internal link pointing at them from anywhere
 * on the site. Home listed only cameras with a live recipe — six of them —
 * so the rest were reachable solely by typing the URL. A crawler had no
 * path to them at all.
 *
 * Full-spec cameras link to their hub. Exposure-only cameras deliberately
 * link to the builder prefilled instead, because they have no hub worth
 * visiting — generating 109 thin spec pages to fix a linking problem would
 * trade one problem for a worse one.
 */
export default function Cameras() {
  const { items: liveRecipes } = usePublished(recipes);
  const { items: liveParams } = usePublished(parameterPages);

  const counts = useMemo(() => {
    const map = {};
    liveRecipes.forEach((r) => {
      map[r.cameraId] = map[r.cameraId] || { recipes: 0, topics: 0 };
      map[r.cameraId].recipes++;
    });
    liveParams.forEach((p) => {
      map[p.cameraId] = map[p.cameraId] || { recipes: 0, topics: 0 };
      map[p.cameraId].topics++;
    });
    return map;
  }, [liveRecipes, liveParams]);

  const byManufacturer = useMemo(() => {
    const groups = {};
    cameras.forEach((c) => {
      (groups[c.manufacturer] = groups[c.manufacturer] || []).push(c);
    });
    Object.values(groups).forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
    // Manufacturers with the most coverage first — that's also roughly the
    // order of how many people are looking for them.
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, []);

  const fullCount = cameras.filter((c) => c.capabilityLevel === "full").length;

  React.useEffect(() => {
    setPageMeta({
      title: `All ${cameras.length} cameras, settings by camera`,
      description: `Every camera, drone, action cam and phone in the database, from DJI and GoPro to Sony and Blackmagic. ${fullCount} with full settings guides.`,
      path: "/cameras/",
      jsonLdId: "cameras-jsonld",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage",
            name: `All ${cameras.length} cameras`,
            description: "Camera settings by device.",
          },
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Cameras", path: "/cameras/" },
          ]),
        ],
      },
    });
  }, [fullCount]);

  return (
    <main className="cameras-page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>Cameras</span>
      </nav>

      <header className="cameras-header">
        <p className="eyebrow">{cameras.length} cameras · {byManufacturer.length} manufacturers</p>
        <h1>Every camera in the database</h1>
        <p className="cameras-sub">
          {fullCount} have full specifications on file and get their own settings guides. The rest
          have confirmed exposure data, aperture, base ISO, filter options, which is enough to
          calculate shutter, ISO and ND for them in the builder.
        </p>
      </header>

      <nav className="cameras-jump" aria-label="Jump to manufacturer">
        {byManufacturer.map(([maker, list]) => (
          <a key={maker} href={`#m-${maker.toLowerCase().replace(/\s+/g, "-")}`}>
            {maker} <span className="jump-count data">{list.length}</span>
          </a>
        ))}
      </nav>

      {byManufacturer.map(([maker, list]) => {
        const anchor = `m-${maker.toLowerCase().replace(/\s+/g, "-")}`;
        return (
          <section className="maker-group" key={maker} aria-labelledby={anchor}>
            <h2 id={anchor}>{maker}</h2>
            <ul className="camera-list">
              {list.map((camera) => {
                const full = camera.capabilityLevel === "full";
                const count = counts[camera.id] || { recipes: 0, topics: 0 };
                const pages = count.recipes + count.topics;
                return (
                  <li key={camera.id} className={full ? "" : "is-exposure-only"}>
                    <Link to={full ? `/cameras/${camera.id}` : `/builder?camera=${camera.id}`}>
                      <span className="camera-name">{camera.name}</span>
                      <span className="camera-meta data">
                        {apertureSummary(camera)}
                        <span className="meta-sep" aria-hidden="true">·</span>
                        {ndSummary(camera)}
                      </span>
                      <span className="camera-action">
                        {full && pages > 0 && <>{pages} {pages === 1 ? "page" : "pages"}</>}
                        {full && pages === 0 && <>Settings guide</>}
                        {!full && (
                          <>
                            <Icon name="aperture" size={13} /> Open in builder
                          </>
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      <div data-prerender-ready="true" style={{ display: "none" }} />
      <Footer />
    </main>
  );
}
